import React, { useState } from 'react'
import Button from '../Button/Button'
import ButtonGroup from '../Button/ButtonGroup/ButtonGroup'
import Paragraph from '../Typography/Paragraph/Paragraph'
import Tooltip from '../Tooltip/Tooltip'
import './ColorSwatch.css'

type versionTypes = 'basic' | 'advanced';

type colorPaletteType = {
  color: string;
  title: string;
};

type ColorSwatchProps = {
  title?: string,
  palette?: colorPaletteType[],
  version?: versionTypes,
  selectedColors?: string[] | colorPaletteType[],
  multiple?: boolean,
  includeDetails?: boolean,
  handleCancel?: () => void,
  handleClear?: () => void,
  handleSelect?: (param: string[] | colorPaletteType[]) => void,
  handleApply?: (param: string[] | colorPaletteType[]) => void
}

const defaultPalette = [
  { color: '#E53935', title: 'Red' },
  { color: '#FB8C00', title: 'Orange' },
  { color: '#FDD835', title: 'Yellow' },
  { color: '#E900B0', title: 'Pink' },
  { color: '#43A047', title: 'Green' },
  { color: '#33C3C0', title: 'Turquoise' },
  { color: '#1E88E5', title: 'Blue' },
  { color: '#8E24AA', title: 'Purple' },
  { color: '#773410', title: 'Brown' },
  { color: '#FFFFFF', title: 'White' },
  { color: '#9E9E9E', title: 'Gray' },
  { color: '#212121', title: 'Black' },
  { color: 'transparent', title: 'Transparent' }
];

const ColorSwatch = ({
  title,
  palette = defaultPalette,
  version = 'advanced',
  selectedColors = [],
  multiple = true,
  includeDetails = false,
  handleCancel,
  handleClear,
  handleSelect,
  handleApply
}: ColorSwatchProps) => {
  const [colorsPicked, setColorsPicked] = useState(multiple ? selectedColors : selectedColors.length > 0 ? [selectedColors[0]] : []);

  // finding color present in colorsPicked based on includeDetails
  const findIsColorPresent = (
    paletteColor: colorPaletteType,
  ): string | colorPaletteType | undefined => {
    return includeDetails
      ? (colorsPicked as colorPaletteType[]).find(c => c?.color === paletteColor.color)
      : (colorsPicked as string[]).find(c => c === paletteColor.color);
  }

  // removing color from colorsPicked based on multiple and includeDetails
  const removeColor = (
    paletteColor: colorPaletteType,
  ): string[] | colorPaletteType[] => {
    if (!multiple) return [];

    return includeDetails
      ? (colorsPicked as colorPaletteType[]).filter(c => c?.color !== paletteColor.color)
      : (colorsPicked as string[]).filter(c => c !== paletteColor.color);
  }

  // adding color to colorsPicked based on multiple and includeDetails
  const addColor = (
    paletteColor: colorPaletteType,
  ): string[] | colorPaletteType[] => {
    if (multiple) {
      return includeDetails
        ? [...(colorsPicked as colorPaletteType[]), paletteColor]
        : [...(colorsPicked as string[]), paletteColor.color];
    } else {
      return includeDetails
        ? [paletteColor]
        : [paletteColor.color];
    }
  }

  const handleSelection = (paletteColor) => {
    const isColorPresent = findIsColorPresent(paletteColor);
    let filteredColors = colorsPicked;

    if (isColorPresent) {
      filteredColors = removeColor(paletteColor);
      setColorsPicked(filteredColors);
    }
    else {
      filteredColors = addColor(paletteColor);
      setColorsPicked(filteredColors);
    }

    if (version === 'basic') {
      handleSelect(filteredColors as string[] | colorPaletteType[]);
    }
  }

  const handleClearAll = () => {
    setColorsPicked([]);
    if (handleClear) {
      handleClear();
    }
  }

  if (version === 'basic') {
    return (
      <div className='ColorSwatch__body'>
        {title && <Paragraph tagName='p' variant='p1' text={title} className='ColorSwatch__title' />}
        <div className='ColorSwatch__palette'>
          <ColorPalette
            colorPalette={palette}
            colorsPicked={colorsPicked}
            includeDetails={includeDetails}
            handleSelection={handleSelection}
          />
        </div>
      </div>
    );
  }

  return (
    <div className='ColorSwatch__container'>
      <div className='ColorSwatch__body'>
        <Paragraph tagName='p' variant='p1' text={title || 'Select a color to filter assets quickly'} className='ColorSwatch__title' />
        <div className='ColorSwatch__palette'>
          <ColorPalette
            colorPalette={palette}
            colorsPicked={colorsPicked}
            includeDetails={includeDetails}
            handleSelection={handleSelection}
          />
        </div>
      </div>
      <div className='ColorSwatch__footer'>
        <Button buttonType='tertiary' version='v2' onClick={() => {
          setColorsPicked([]);
          handleCancel();
        }}>Cancel</Button>
        <ButtonGroup>
          <Button buttonType='secondary' version='v2' className='ColorSwatch__btn' disabled={!colorsPicked.length} onClick={handleClearAll}>Clear All</Button>
          <Button buttonType='primary' version='v2' className='ColorSwatch__btn' disabled={!colorsPicked.length} onClick={() => handleApply(colorsPicked as string[] | colorPaletteType[])}>Apply</Button>
        </ButtonGroup>
      </div>
    </div>
  )
}

const ColorPalette = ({ colorPalette, colorsPicked, includeDetails, handleSelection }) => {
  const [isTooltip, setIsTooltip] = useState('');

  const isColorActive = (paletteColor) => {
    return includeDetails ?
      colorsPicked.find(c => c?.color === paletteColor.color) :
      colorsPicked.find(c => c === paletteColor.color);
  }

  return (
    <>
      {
        colorPalette.map((paletteColor, index) =>
          <Tooltip
            key={`${paletteColor.color}${paletteColor.title}${index}`}
            content={paletteColor.title}
            position='bottom'
            showArrow={true}
            variantType='basic'
            type='secondary'
            visible={paletteColor.color === isTooltip}
            className='ColorSwatch__tooltip'
            wrapperElementType='span'
            style={{ paddingBottom: 0 }}
          >
            <div
              key={paletteColor.color}
              className={`ColorSwatch__palette__container ${isColorActive(paletteColor) ? 'active' : ''}`}
              onClick={() => handleSelection(paletteColor)}
              onMouseOver={() => setIsTooltip(paletteColor.color)}
              onMouseLeave={() => setIsTooltip('')}
            >
              <div className='ColorSwatch__palette__placeholder'>
                <div className='ColorSwatch__palette__color' style={{ background: paletteColor.color }}>
                  {paletteColor.color === 'transparent' && <TransparentSVG />}
                </div>
              </div>
            </div>
          </Tooltip>
        )
      }
    </>
  );
}

const TransparentSVG = () => {
  return (
    <div className='ColorSwatch__transparent__svg'>
      <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g opacity="0.75">
          <path d="M5.77778 26H2.88889V23.1111H5.77778V26Z" fill="#6E6B86" />
          <path d="M11.5556 26H8.66667V23.1111H11.5556V26Z" fill="#6E6B86" />
          <path d="M17.3333 26H14.4444V23.1111H17.3333V26Z" fill="#6E6B86" />
          <path d="M23.1111 26H20.2222V23.1111H23.1111V26Z" fill="#6E6B86" />
          <path d="M2.88889 23.1111H0V20.2222H2.88889V23.1111Z" fill="#6E6B86" />
          <path d="M8.66667 23.1111H5.77778V20.2222H8.66667V23.1111Z" fill="#6E6B86" />
          <path d="M14.4444 23.1111H11.5556V20.2222H14.4444V23.1111Z" fill="#6E6B86" />
          <path d="M20.2222 23.1111H17.3333V20.2222H20.2222V23.1111Z" fill="#6E6B86" />
          <path d="M26 23.1111H23.1111V20.2222H26V23.1111Z" fill="#6E6B86" />
          <path d="M5.77778 20.2222H2.88889V17.3333H5.77778V20.2222Z" fill="#6E6B86" />
          <path d="M11.5556 20.2222H8.66667V17.3333H11.5556V20.2222Z" fill="#6E6B86" />
          <path d="M17.3333 20.2222H14.4444V17.3333H17.3333V20.2222Z" fill="#6E6B86" />
          <path d="M23.1111 20.2222H20.2222V17.3333H23.1111V20.2222Z" fill="#6E6B86" />
          <path d="M2.88889 17.3333H0V14.4444H2.88889V17.3333Z" fill="#6E6B86" />
          <path d="M8.66667 17.3333H5.77778V14.4444H8.66667V17.3333Z" fill="#6E6B86" />
          <path d="M14.4444 17.3333H11.5556V14.4444H14.4444V17.3333Z" fill="#6E6B86" />
          <path d="M20.2222 17.3333H17.3333V14.4444H20.2222V17.3333Z" fill="#6E6B86" />
          <path d="M26 17.3333H23.1111V14.4444H26V17.3333Z" fill="#6E6B86" />
          <path d="M5.77778 14.4444H2.88889V11.5556H5.77778V14.4444Z" fill="#6E6B86" />
          <path d="M11.5556 14.4444H8.66667V11.5556H11.5556V14.4444Z" fill="#6E6B86" />
          <path d="M17.3333 14.4444H14.4444V11.5556H17.3333V14.4444Z" fill="#6E6B86" />
          <path d="M23.1111 14.4444H20.2222V11.5556H23.1111V14.4444Z" fill="#6E6B86" />
          <path d="M2.88889 11.5556H0V8.66667H2.88889V11.5556Z" fill="#6E6B86" />
          <path d="M8.66667 11.5556H5.77778V8.66667H8.66667V11.5556Z" fill="#6E6B86" />
          <path d="M14.4444 11.5556H11.5556V8.66667H14.4444V11.5556Z" fill="#6E6B86" />
          <path d="M20.2222 11.5556H17.3333V8.66667H20.2222V11.5556Z" fill="#6E6B86" />
          <path d="M26 11.5556H23.1111V8.66667H26V11.5556Z" fill="#6E6B86" />
          <path d="M5.77778 8.66667H2.88889V5.77778H5.77778V8.66667Z" fill="#6E6B86" />
          <path d="M11.5556 8.66667H8.66667V5.77778H11.5556V8.66667Z" fill="#6E6B86" />
          <path d="M17.3333 8.66667H14.4444V5.77778H17.3333V8.66667Z" fill="#6E6B86" />
          <path d="M23.1111 8.66667H20.2222V5.77778H23.1111V8.66667Z" fill="#6E6B86" />
          <path d="M2.88889 5.77778H0V2.88889H2.88889V5.77778Z" fill="#6E6B86" />
          <path d="M8.66667 5.77778H5.77778V2.88889H8.66667V5.77778Z" fill="#6E6B86" />
          <path d="M14.4444 5.77778H11.5556V2.88889H14.4444V5.77778Z" fill="#6E6B86" />
          <path d="M20.2222 5.77778H17.3333V2.88889H20.2222V5.77778Z" fill="#6E6B86" />
          <path d="M26 5.77778H23.1111V2.88889H26V5.77778Z" fill="#6E6B86" />
          <path d="M5.77778 2.88889H2.88889V0H5.77778V2.88889Z" fill="#6E6B86" />
          <path d="M11.5556 2.88889H8.66667V0H11.5556V2.88889Z" fill="#6E6B86" />
          <path d="M17.3333 2.88889H14.4444V0H17.3333V2.88889Z" fill="#6E6B86" />
          <path d="M23.1111 2.88889H20.2222V0H23.1111V2.88889Z" fill="#6E6B86" />
        </g>
      </svg>
    </div>
  );
}


export default ColorSwatch